import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Customer } from '@usf/customer-types';
import {
  OrderETAMessageType,
  OrderHeader,
  OrderItem,
  OrderStatus,
} from '@usf/ngrx-order';
import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { FEATURES } from 'src/app/shared/constants/splitio-features';
import { Product } from 'src/app/shared/models/product.model';
import { OrderStatusDetails } from '../../../../shared/constants/order-status-title.enum';
import {
  SubbedItem,
  SubmittedOrderTallies,
  SubmittedOrderViewModel,
} from '../../../models/submitted-order-view-model';
import { OrderService } from '../../../services/order.service';
import { WMTService } from '@shared/services/wmt/wmt.service';
import { OrderExceptionService } from '@order/pages/order-exceptions/service/order-exception.service';
import { OrderMinimumViewModel } from 'src/app/order/models/order-minimum-view-model';
import { OrderPunchoutService } from 'src/app/order/services/order-punchout.service';
import { DownloadOrdersEvent } from 'src/app/order/models/download-orders-event';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';

@Component({
  selector: 'app-submitted-order-info-card',
  templateUrl: './submitted-order-info-card.component.html',
  styleUrls: ['./submitted-order-info-card.component.scss'],
})
export class SubmittedOrderInfoCardComponent implements OnInit {
  @Input() orderItems: OrderItem[];
  @Input() products: Map<number, Product>;
  @Input() customer: Customer;
  @Input() tallies: SubmittedOrderTallies;
  @Input() orderStatusDetails: OrderStatusDetails;
  @Input() isDelivered?: boolean;
  @Input() deliveryETA?: string;
  @Input() actualDeliveryDate?: Date;
  @Input() displayTitle: string;
  @Input() isOnline$: Observable<boolean>;
  @Input() restrictToOG: boolean;
  @Input() isDealsAvailable: boolean;
  @Input() willCallInFlight: boolean;
  @Input() shipToInFlight: boolean;
  @Input() customerTimeZone: string;
  @Input() autoSubItems: Map<number, SubbedItem>;
  @Input() platformType: string;
  @Input() isEditable: boolean;
  @Input() isCancellable: boolean;
  @Input() transferButtonText: string;
  @Input() showTransferButton: boolean;
  @Input() modifyButtonText: string;
  @Input() isInternalUser: boolean;
  @Input() orderStatusDisplayEnabled: boolean;
  @Input() customersWithTheSameDivision: number;
  @Input() shipToCustomer: Customer;
  @Input() shipToDepartmentName: string;
  @Input() isProntoDeliveryCustomer: boolean;
  @Input() isWmtTimezoneFeatureFlag: boolean;
  private _orderHeader: OrderHeader;
  @Input() set orderHeader(oH: OrderHeader) {
    this._orderHeader = oH;
    this.getMinimumOrderMetDetails();
  }
  get orderHeader(): OrderHeader {
    return this._orderHeader;
  }

  @Output() downloadModalOpenEmitter = new EventEmitter();
  @Output() downloadFileEmmitter = new EventEmitter<DownloadOrdersEvent>();

  @Output() shareOrderEmitter = new EventEmitter();
  @Output() printOrderEmitter = new EventEmitter();
  @Output() editSubmittedOrderEmitter = new EventEmitter();
  @Output() cancelOrderModalEmitter = new EventEmitter();
  @Output() shipToModalOpenEmitter = new EventEmitter();
  @Output() willCallModalOpenEmitter = new EventEmitter();
  @Output() recoveryModalOpenEmitter = new EventEmitter();
  @Output() transferModalEmitter = new EventEmitter();

  confirmModalOpen = false;
  showFullInfo = true;
  isOnMinimumOrderMetFlag: boolean;
  isWillCallTypeFlagOn: boolean;
  orderMinimumDetails: OrderMinimumViewModel;
  hideEtaGlobalFeatureFlag$: Observable<boolean>;
  readonly orderETAMessageType = OrderETAMessageType;
  readonly orderStatusEnum = OrderStatus;

  readonly insightsOrderLinkFeatureFlag =
    FEATURES.split_global_order_insight.name;
  readonly recoveryBtnDivisionFeatureFlag =
    FEATURES.split_division_order_recovery_button.name;

  readonly features = FEATURES;

  vm: SubmittedOrderViewModel;
  constructor(
    readonly platformService: PlatformService,
    private orderService: OrderService,
    private wmtService: WMTService,
    readonly orderExceptionService: OrderExceptionService,
    readonly panAppState: PanAppState,
    private orderPunchoutService: OrderPunchoutService,
    private navigationHelperService: NavigationHelperService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.panAppState.feature$([FEATURES.split_global_min_order_met.name]),
    ])
      .pipe(take(1))
      .subscribe(([minFlag]) => {
        this.isOnMinimumOrderMetFlag = minFlag;
        this.getMinimumOrderMetDetails();
      });
    this.hideEtaGlobalFeatureFlag$ = this.panAppState.feature$([
      FEATURES.split_global_order_hide_eta.name,
    ]);
  }

  getMinimumOrderMetDetails() {
    if (
      !!this.orderHeader &&
      this.isOnMinimumOrderMetFlag &&
      !this.orderMinimumDetails
    ) {
      this.isOnMinimumOrderMetFlag = false;
      this.orderService
        .loadOrderMinimumViewModel$(this.orderHeader)
        .pipe(take(1))
        .subscribe((vm: OrderMinimumViewModel) => {
          this.isOnMinimumOrderMetFlag = true;
          this.orderMinimumDetails = vm;
        });
    }
  }

  get hasETADetails(): boolean {
    return (
      !!this.orderHeader?.etaRangeMaximum &&
      !!this.orderHeader?.etaRangeMinimum &&
      this.isValidDate(this.orderHeader?.etaRangeMaximum) &&
      this.isValidDate(this.orderHeader?.etaRangeMinimum)
    );
  }

  get isWillCall(): boolean {
    return this.orderHeader?.orderType === 'WC';
  }

  convertTz(date: string | Date): Date {
    const tzCode = this.wmtService?.translateTZCode(this.customerTimeZone);
    return new Date(
      new Date(date?.toString()?.replace('Z', '')).toLocaleString('en-US', {
        timeZone: tzCode,
      }),
    );
  }

  downloadOrderModalOpen() {
    this.downloadModalOpenEmitter.emit();
  }
  shipToModalOpen() {
    this.shipToModalOpenEmitter.emit();
  }

  recoveryModalOpen() {
    this.recoveryModalOpenEmitter.emit();
  }
  willCallModalOpen() {
    this.willCallModalOpenEmitter.emit();
  }

  printOrder(event: Event) {
    this.printOrderEmitter.emit({
      event,
    });
  }

  shareOrder(event: Event) {
    this.shareOrderEmitter.emit({
      event,
    });
  }

  navigateToOrderExceptions() {
    this.orderExceptionService?.navigateToResolveExceptionsPage(
      this.orderHeader,
    );
  }

  navigateToOrderInsightsPage() {
    const orderId = `${this.orderHeader.customerNumber}:${this.orderHeader.departmentNumber}:${this.orderHeader.orderId}`;

    this.navigationHelperService.routeToOrderInsights(orderId);
  }

  collapseExpandDealsInfo() {
    this.showFullInfo = !this.showFullInfo;
  }

  editSubmittedOrder() {
    this.editSubmittedOrderEmitter.emit(true);
  }

  cancelOrderModal() {
    this.cancelOrderModalEmitter.emit(true);
  }

  transferOrderModal() {
    this.transferModalEmitter.emit(true);
  }

  doPunchoutTransfer(isRetransfer: boolean = false) {
    this.orderPunchoutService.tracePunchoutTransferClick();
    this.orderPunchoutService.doPunchoutTransfer(
      [{ orderHeader: this.orderHeader, orderItems: this.orderItems }],
      isRetransfer,
    );
  }

  get insightsItems() {
    return (this.orderHeader?.dyfProductsNotOrdered || []).concat(
      this.orderHeader?.newProductsOrderedNotInRecentlyPurchased || [],
    );
  }

  isValidDate(dateToValidate: any): boolean {
    try {
      if (new Date(dateToValidate).toString() === 'Invalid Date') return false;
    } catch (e) {
      return false;
    }
    return true;
  }

  isSuperUser$(): Observable<boolean> {
    return this.orderService.isSuperUser$();
  }
}
